import React, { useEffect, useState } from "react";
import { Row, Col, Table, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label } from "reactstrap";
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from 'xlsx';
import salesMembershipService from "../../services/api/sales-membership-service";

const AVAILABLE_PLAN_SALES_EXPORT_FIELDS = [
  { label: "Plan ID", key: "plan_id" },
  { label: "Price", key: "plan_price" },
  { label: "Sales Count", key: "sales_count" },
  { label: "Sales Amount", key: "sales_amount" },
  { label: "Sales Amount (INR)", key: "sales_amount_inr" },
  { label: "Platform Fee (INR)", key: "platform_fee" },
  { label: "PG Commission (INR)", key: "pg_commission" },
  { label: "Gross Amount (INR)", key: "gross_amount" },
  { label: "GST Amount (INR)", key: "gst_amount" },
  { label: "Net Amount (INR)", key: "net_amount" },
  { label: "Publisher Share", key: "publisher_share_amount" },
  { label: "Mediology Share", key: "mediology_share_amount" },
];

const CellValue = ({ value, loading, fixed = 2, currency }) => {
  if (loading) return <Spinner size="sm" />;
  const num = Number(value || 0);
  const formatted = num.toFixed(fixed);
  if (currency === 'INR') return `₹ ${formatted}`;
  if (currency === 'USD') return `$ ${formatted}`;
  return formatted;
};

const PlanSalesTable = ({ formState }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  
  // Modal states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFields, setExportFields] = useState(AVAILABLE_PLAN_SALES_EXPORT_FIELDS.map(f => f.key));
  const [exportError, setExportError] = useState("");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [excelBlob, setExcelBlob] = useState(null);
  const [fileName, setFileName] = useState("");
  const [orderedFields, setOrderedFields] = useState([...AVAILABLE_PLAN_SALES_EXPORT_FIELDS]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);
  const INR_PLAN_IDS = [
    'plan_inr_epaper_combo_yearly', 
    'plan_inr_monthly', 
    'plan_inr_yearly',
    'plan_inr_epaper_combo_30_months'
  ];
  const USD_PLAN_IDS = [
    'plan_usd_epaper_combo_yearly', 
    'plan_usd_monthly', 
    'plan_usd_yearly',
    'plan_usd_epaper_combo_30_months'
  ];

  // Modal handler functions
  const toggleExportModal = () => {
    // Reset to initial field order on modal close
    setOrderedFields([...AVAILABLE_PLAN_SALES_EXPORT_FIELDS]);
    setExportModalOpen(!exportModalOpen);
    setExportError("");
    setIsPreviewMode(false);
    setPreviewData([]);
    setPreviewColumns([]);
    setExcelBlob(null);
    setFileName("");
  };

  const handleSelectAll = () => {
    if (exportFields.length === orderedFields.length) {
      setExportFields([]);
    } else {
      setExportFields(orderedFields.map(field => field.key));
    }
  };

  const handleFieldSelection = (e, fieldKey) => {
    if (e.target.checked) {
      setExportFields([...exportFields, fieldKey]);
    } else {
      setExportFields(exportFields.filter(f => f !== fieldKey));
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e, index) => {
    setDraggedOverItem(index);
  };

  const handleDragLeave = () => {
    setDraggedOverItem(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== index) {
      const newOrderedFields = [...orderedFields];
      const draggedField = newOrderedFields[draggedItem];
      newOrderedFields.splice(draggedItem, 1);
      newOrderedFields.splice(index, 0, draggedField);
      setOrderedFields(newOrderedFields);
    }
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleExport = () => {
    if (excelBlob && fileName) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(excelBlob);
      link.download = fileName;
      link.click();
    } else {
      handlePreview();
    }
  };

  const handlePreview = async () => {
    if (exportFields.length === 0) {
      setExportError("Please select at least one field to export.");
      return;
    }

    try {
      const selectedOrderedFields = orderedFields
        .filter(field => exportFields.includes(field.key))
        .map(field => field.key);

      console.log("Selected fields for export:", selectedOrderedFields);

      if (!data || data.length === 0) {
        setExportError("No data available to export");
        return;
      }

      setExportError("Loading preview...");

      // Get selected fields with their labels
      const selectedFieldsWithLabels = orderedFields
        .filter(field => exportFields.includes(field.key));

      // Prepare data for export
      const exportData = data.map((item, index) => {
        const exportRow = { 'S.No.': index + 1 };
        selectedFieldsWithLabels.forEach(field => {
          if (field.key === 'plan_id') {
            const planId = item.plan_id || 'N/A';
            exportRow[field.label] = USD_PLAN_IDS.includes(item.plan_id) 
              ? `${planId}` 
              : planId;
          }
          if (field.key === 'plan_price') {
            const fixed = USD_PLAN_IDS.includes(item.plan_id) ? 2 : 0;
            const currency = USD_PLAN_IDS.includes(item.plan_id) ? "$" : "₹";
            const basePrice = ` ${Number(item?.plan_info?.amount || 0).toFixed(fixed)}`;
            
            // Add premium component for specific plans
            if (['plan_inr_epaper_combo_yearly', 'plan_usd_epaper_combo_yearly', 'plan_inr_epaper_combo_30_months', 'plan_usd_epaper_combo_30_months'].includes(item.plan_id)) {
              const premiumComponent = ` [${currency}${item?.plan_info?.membership_share} - Premium Component]`;
              exportRow[field.label] = basePrice + premiumComponent;
            } else {
              exportRow[field.label] = basePrice;
            }
          }
          if (field.key === 'sales_count') exportRow[field.label] = item.sales_count || 0;
          if (field.key === 'sales_amount') exportRow[field.label] = ` ${Number(item.sales_amount || 0).toFixed(2)}`;
          if (field.key === 'sales_amount_inr') exportRow[field.label] = ` ${Number(item.sales_amount_inr || 0).toFixed(2)}`;
          if (field.key === 'platform_fee') exportRow[field.label] = ` ${Number(item.platform_fee || 0).toFixed(2)}`;
          if (field.key === 'pg_commission') exportRow[field.label] = ` ${Number(item.pg_commission || 0).toFixed(2)}`;
          if (field.key === 'gross_amount') exportRow[field.label] = ` ${Number(item.gross_amount || 0).toFixed(2)}`;
          if (field.key === 'gst_amount') exportRow[field.label] = ` ${Number(item.gst_amount || 0).toFixed(2)}`;
          if (field.key === 'net_amount') exportRow[field.label] = ` ${Number(item.net_amount || 0).toFixed(2)}`;
          if (field.key === 'publisher_share_amount') exportRow[field.label] = ` ${Number(item.publisher_share_amount || 0).toFixed(2)}`;
          if (field.key === 'mediology_share_amount') exportRow[field.label] = ` ${Number(item.mediology_share_amount || 0).toFixed(2)}`;
        });
        return exportRow;
      });

      // Add totals row if available
      if (totals && Object.keys(totals).length > 0) {
        const totalsRow = { 'S.No.': '' };
        selectedFieldsWithLabels.forEach(field => {
          if (field.key === 'plan_id') totalsRow[field.label] = 'TOTALS';
          if (field.key === 'plan_price') totalsRow[field.label] = '';
          if (field.key === 'sales_count') totalsRow[field.label] = totals.total_sales_count || 0;
          // if (field.key === 'sales_amount') totalsRow[field.label] = ` ${Number(totals.total_sales_amount || 0).toFixed(2)}`;
          if (field.key === 'sales_amount_inr') totalsRow[field.label] = `₹ ${Number(totals.total_sales_amount_inr || 0).toFixed(2)}`;
          if (field.key === 'platform_fee') totalsRow[field.label] = `₹ ${Number(totals.total_platform_fee || 0).toFixed(2)}`;
          if (field.key === 'pg_commission') totalsRow[field.label] = `₹ ${Number(totals.total_pg_commission || 0).toFixed(2)}`;
          if (field.key === 'gross_amount') totalsRow[field.label] = `₹ ${Number(totals.total_gross_amount || 0).toFixed(2)}`;
          if (field.key === 'gst_amount') totalsRow[field.label] = `₹ ${Number(totals.total_gst_amount || 0).toFixed(2)}`;
          if (field.key === 'net_amount') totalsRow[field.label] = `₹ ${Number(totals.total_net_amount || 0).toFixed(2)}`;
          if (field.key === 'publisher_share_amount') totalsRow[field.label] = `₹ ${Number(totals.total_publisher_share_amount || 0).toFixed(2)}`;
          if (field.key === 'mediology_share_amount') totalsRow[field.label] = `₹ ${Number(totals.total_mediology_share_amount || 0).toFixed(2)}`;
        });
        exportData.push(totalsRow);
      }

      console.log("Prepared export data:", exportData);

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Plan Sales Data');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Plan_Sales_Data_${currentDate}.xlsx`;

      // Create blob
      const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Save blob and filename for later download
      setExcelBlob(blob);
      setFileName(fileName);

      // Update state with preview data
      setPreviewData(exportData);
      
      // Get proper column labels for preview
      const previewColumnLabels = selectedFieldsWithLabels.map(field => field.label);
      
      setPreviewColumns(['S.No.', ...previewColumnLabels]);
      setIsPreviewMode(true);
      setExportError("");

      console.log("Preview mode activated successfully");
    } catch (err) {
      console.error("Error generating preview:", err);
      setExportError(`Error: ${err.message || 'Failed to generate preview. Please try again later.'}`);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Configure formState with specific plan IDs for this table
        const planSalesFormState = {
          ...formState, plan_id: [...INR_PLAN_IDS, ...USD_PLAN_IDS]
        };
        
        const response = await salesMembershipService.getPlanWiseSales(planSalesFormState);
        
        if (cancelled) return;

        if (response?.status && response.data) {
          setData(response.data.data);
          
          // Calculate totals on frontend as fallback if backend totals are incomplete
          const backendTotals = response.data.totals || {};
          const frontendTotals = response.data.data.reduce((acc, item) => {
            acc.total_sales_count += item.sales_count || 0;
            acc.total_sales_amount += item.sales_amount || 0;
            acc.total_sales_amount_inr += item.sales_amount_inr || 0;
            acc.total_platform_fee += item.platform_fee || 0;
            acc.total_pg_commission += item.pg_commission || 0;
            acc.total_gross_amount += item.gross_amount || 0;
            acc.total_gst_amount += item.gst_amount || 0;
            acc.total_net_amount += item.net_amount || 0;
            acc.total_marketing_fee += item.marketing_fee || 0;
            acc.total_publisher_share_amount += item.publisher_share_amount || 0;
            acc.total_mediology_share_amount += item.mediology_share_amount || 0;
            return acc;
          }, {
            total_sales_count: 0,
            total_sales_amount: 0,
            total_sales_amount_inr: 0,
            total_platform_fee: 0,
            total_pg_commission: 0,
            total_gross_amount: 0,
            total_gst_amount: 0,
            total_net_amount: 0,
            total_marketing_fee: 0,
            total_publisher_share_amount: 0,
            total_mediology_share_amount: 0
          });
          
          // Use frontend calculated totals if backend totals are incomplete
          const finalTotals = Object.keys(backendTotals).length > 2 ? backendTotals : frontendTotals;
          setTotals(finalTotals);
          
          console.log('Plan Sales Data:', response.data.data);
          console.log('Backend Totals:', backendTotals);
          console.log('Frontend Totals:', frontendTotals);
          console.log('Final Totals:', finalTotals);
        } else {
          setData([]);
          setTotals({});
        }
      } catch (error) {
        console.error('Error fetching plan sales data:', error);
        setData([]);
        setTotals({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    
    return () => { cancelled = true; };
  }, [formState]);

  return (
    <div className="mb-3">
      <Row>
        <Col md="12">
          <div>
            {loading ? (
              <div className="text-center py-3">
                <Spinner size="sm" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-3 text-muted">
                No data available
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered mb-0" style={{ width: '100%', minWidth: '1000px', marginBottom: '0' }}>
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '180px', fontSize: '0.8rem' }}>Plan ID</th>
                      <th style={{ width: '1200px', fontSize: '0.8rem' }}>Price</th>
                      <th style={{ width: '70px', fontSize: '0.8rem' }}>Count</th>
                      <th style={{ width: '90px', fontSize: '0.8rem' }}>Sales Amt</th>
                      <th style={{ width: '90px', fontSize: '0.8rem' }}>Sales (INR)</th>
                      <th style={{ width: '80px', fontSize: '0.8rem' }}>GST</th>
                      <th style={{ width: '80px', fontSize: '0.8rem' }}>PG Comm</th>
                      <th style={{ width: '100px', fontSize: '0.8rem' }}>Gross Amt (INR)</th>
                      <th style={{ width: '120px', fontSize: '0.8rem' }}>Platform Fee (17.5%)</th>
                      <th style={{ width: '90px', fontSize: '0.8rem' }}>Net Amt (INR)</th>
                      <th style={{ width: '100px', fontSize: '0.8rem' }}>Publisher (INR)</th>
                      <th style={{ width: '100px', fontSize: '0.8rem' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>Mediology (INR)</span>
                          <FaFileExcel 
                            className="text-success" 
                            style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                            onClick={toggleExportModal}
                            title="Export to Excel"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            {item.plan_id}
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item?.plan_info?.amount} loading={false} fixed={USD_PLAN_IDS.includes(item.plan_id) ? 2 : 0} currency={USD_PLAN_IDS.includes(item.plan_id) ? "USD" : "INR"} />
                            {" "}
                            {['plan_inr_epaper_combo_yearly', 'plan_usd_epaper_combo_yearly', 'plan_inr_epaper_combo_30_months', 'plan_usd_epaper_combo_30_months'].includes(item.plan_id) && (
                              <span style={{ fontSize: '0.65rem'}}>
                                [{USD_PLAN_IDS.includes(item.plan_id) ? "$" : "₹"}{item?.plan_info?.membership_share} - Premium Component]
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.sales_count} loading={false} fixed={0} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue 
                              value={item.sales_amount} 
                              loading={false} 
                              fixed={2} 
                              currency={USD_PLAN_IDS.includes(item.plan_id) ? "USD" : "INR"}
                            />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.sales_amount_inr} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.gst_amount} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.pg_commission} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.gross_amount} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.platform_fee} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.net_amount} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.publisher_share_amount} loading={false} fixed={2} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold">
                            <CellValue value={item.mediology_share_amount} loading={false} fixed={2} />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    {data.length > 0 && totals && Object.keys(totals).length > 0 && (
                      <tr className="bg-light">
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">Totals</div>
                        </td>
                        {/* No totals for Plan Price and Sales Amt */}
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark"></div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_sales_count} loading={false} fixed={0} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark"></div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_sales_amount_inr} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_gst_amount} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_pg_commission} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_gross_amount} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_platform_fee} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_net_amount} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_publisher_share_amount} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          <div className="fw-bold text-dark">
                            <CellValue value={totals.total_mediology_share_amount} loading={false} fixed={2} currency={"INR"} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Export Modal */}
      <Modal
        isOpen={exportModalOpen}
        centered
        size={isPreviewMode ? "xl" : "md"}
      >
        <ModalHeader className="bg-light text-center">
          {isPreviewMode ? "Preview Plan Sales Excel Data" : "Export Plan Sales Data"}
        </ModalHeader>
        <ModalBody>
          {!isPreviewMode ? (
            <FormGroup>
              <Label className="fw-bold mb-2">
                Select and Order Fields to Add in Excel File
              </Label>
              <div className="mb-3 alert alert-info">
                <small>
                  <strong>Tip:</strong> The fields will appear in your Excel file in the order shown below.
                  <strong> Drag and drop</strong> fields to reorder them.
                </small>
              </div>
              <div
                className="border rounded p-2 overflow-auto"
                style={{ maxHeight: "300px" }}
              >
                <div className="fs mb-2 d-flex align-items-center">
                  <input
                    className="form-check-input mt-0"
                    type="checkbox"
                    id="selectAll"
                    onChange={handleSelectAll}
                    checked={exportFields.length === orderedFields.length}
                  />
                  <label
                    className="form-check-label ms-2 mb-0"
                    htmlFor="selectAll"
                  >
                    Select All
                  </label>
                </div>

                {orderedFields.map((field, index) => (
                  <div
                    className={`fs d-flex align-items-center justify-content-between border-bottom py-2 ${draggedItem === index ? 'opacity-50' : ''} ${draggedOverItem === index ? 'bg-light border border-primary' : ''}`}
                    key={field.key}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{ cursor: 'grab' }}
                  >
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="drag-handle me-3 text-secondary d-flex align-items-center" style={{ cursor: 'grab' }}>
                        <i className="fa fa-bars"></i>
                      </div>
                      <div className="d-flex align-items-center">
                        <input
                          className="form-check-input mt-0"
                          type="checkbox"
                          id={field.key}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleFieldSelection(e, field.key)}
                          checked={exportFields.includes(field.key)}
                        />
                        <label
                          className="form-check-label ms-2 mb-0"
                          htmlFor={field.key}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {field.label}
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </FormGroup>
          ) : (
            <div className="excel-preview">
              <div className="table-responsive" style={{ maxHeight: "400px" }}>
                <table className="table table-striped table-bordered">
                  <thead className="bg-light">
                    <tr>
                      {previewColumns.map((column, index) => (
                        <th key={index}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 50).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {previewColumns.map((column, colIndex) => (
                          <td key={colIndex}>{row[column]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewData.length > 50 && (
                <div className="text-center text-muted mt-2">
                  Showing first 50 records of {previewData.length} total records.
                </div>
              )}
            </div>
          )}
          {exportError && (
            <div className="text-danger text-center mt-3 small">
              {exportError}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          {!isPreviewMode ? (
            <>
              <Button color="primary" onClick={handlePreview}>
                Preview
              </Button>
              <Button color="secondary" onClick={toggleExportModal}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button color="success" onClick={handleExport}>
                Download Excel
              </Button>
              <Button color="primary" onClick={() => setIsPreviewMode(false)}>
                Back to Fields
              </Button>
              <Button color="secondary" onClick={toggleExportModal}>
                Cancel
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PlanSalesTable; 